import { useCallback, useSyncExternalStore } from "react";

/**
 * The signed-in layout's per-browser choices: the menu along the top ("top",
 * the default for everyone since 26 Oct 2026) or on the left ("side"), and whether the side menu is
 * collapsed to icons. Kept in localStorage so they survive a reload.
 */
export type MenuLayout = "side" | "top";

const LAYOUT_KEY = "rai.layout";
const COLLAPSED_KEY = "rai.sidebar";
const listeners = new Set<() => void>();

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked: the choice lasts until reload
  }
  memory.set(key, value);
  for (const l of listeners) l();
}

// fallback when storage is blocked, and the value the hooks read
const memory = new Map<string, string>();

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function value(key: string): string | null {
  return memory.get(key) ?? read(key);
}

export function useMenuLayout(): [MenuLayout, (l: MenuLayout) => void] {
  const v = useSyncExternalStore(subscribe, () => value(LAYOUT_KEY));
  const set = useCallback((l: MenuLayout) => write(LAYOUT_KEY, l), []);
  return [v === "side" ? "side" : "top", set];
}

export function useSidebarCollapsed(): [boolean, (c: boolean) => void] {
  const v = useSyncExternalStore(subscribe, () => value(COLLAPSED_KEY));
  const set = useCallback(
    (c: boolean) => write(COLLAPSED_KEY, c ? "collapsed" : "open"),
    [],
  );
  return [v === "collapsed", set];
}
